package com.dotpx.ambient

import kotlin.math.abs
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin

/** Fixed-storage combustion model shared by full ambience and preview rendering. */
internal class FireTextureState(sampleRate: Double) {
    private val microThreshold = (1.0 - 15.0 / sampleRate).toFloat()
    private val clusterThreshold = (1.0 - 1.0 / sampleRate).toFloat()
    private val tickDecay = exp(-1.0 / (0.000_12 * sampleRate)).toFloat()
    private val burstDecay = exp(-1.0 / (0.003_5 * sampleRate)).toFloat()
    private val tailDecay = exp(-1.0 / (0.026 * sampleRate)).toFloat()
    private val childMinFrames = max((0.005 * sampleRate).toInt(), 1)
    private val childSpanFrames = max((0.025 * sampleRate).toInt(), 1)

    private var tick = 0f
    private var burstEnvelope = 0f
    private var tailEnvelope = 0f
    private var clusterChildren = 0
    private var framesUntilChild = 0
    var sideActivity = 0f
        private set

    fun reset() {
        tick = 0f
        burstEnvelope = 0f
        tailEnvelope = 0f
        clusterChildren = 0
        framesUntilChild = 0
        sideActivity = 0f
    }

    fun nextSample(
        noise: Float,
        high: Float,
        commonFast: Float,
        commonSlow: Float,
        commonDeep: Float,
        motion: Float,
    ): Float {
        if (abs(noise) > microThreshold) {
            excite(abs(high), if (noise < 0f) -1f else 1f)
            if (abs(noise) > clusterThreshold) {
                val strength = min(abs(high) * 0.5f, 1f)
                clusterChildren = 2 + (strength * 6f).toInt()
                framesUntilChild = childMinFrames + (strength * childSpanFrames.toFloat()).toInt()
            }
        }
        if (clusterChildren > 0) {
            framesUntilChild -= 1
            if (framesUntilChild <= 0) {
                val descriptor = min(abs(noise - commonFast) * 1.7f, 2f)
                excite(descriptor, if (noise < 0f) -1f else 1f)
                clusterChildren -= 1
                framesUntilChild = childMinFrames + (abs(noise) * childSpanFrames.toFloat()).toInt()
            }
        }

        val fractureNoise = noise * 0.5f + (noise - high) * 0.5f
        val brightFractureNoise = fractureNoise * 0.8f + high * 0.2f
        sideActivity = min(abs(tick) * 1.8f + burstEnvelope * 1.2f + tailEnvelope * 0.4f, 1f)
        val fracture = tick * 3.20f + brightFractureNoise * burstEnvelope * 2.40f +
            brightFractureNoise * tailEnvelope * 0.78f
        tick *= tickDecay
        burstEnvelope *= burstDecay
        tailEnvelope *= tailDecay
        if (abs(tick) < 0.000_000_01f) tick = 0f
        if (burstEnvelope < 0.000_000_01f) burstEnvelope = 0f
        if (tailEnvelope < 0.000_000_01f) tailEnvelope = 0f
        val combustion = (commonSlow - commonDeep) * 1.12f + commonDeep * 0.20f
        val midFlame = (commonFast - commonSlow) * (0.032f + motion * 0.018f)
        return combustion + midFlame + fracture
    }

    private fun excite(descriptor: Float, polarity: Float) {
        val normalized = min(descriptor * 0.5f, 1f)
        val squared = normalized * normalized
        val amplitude = 0.012f + 0.88f * squared * squared
        val selector = abs(sin(descriptor.toDouble() * 91.731)).toFloat()
        when {
            selector < 0.649_448f -> burstEnvelope = max(burstEnvelope, amplitude * 0.16f)
            selector < 0.951_057f -> {
                tick += polarity * amplitude * 0.82f
                burstEnvelope = max(burstEnvelope, amplitude * 0.70f)
                tailEnvelope = max(tailEnvelope, amplitude * 0.36f)
            }
            selector < 0.999_507f -> tick += polarity * amplitude
            else -> {
                burstEnvelope = max(burstEnvelope, amplitude * 0.92f)
                tailEnvelope = max(tailEnvelope, amplitude * 0.45f)
            }
        }
    }
}
